import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

// List of free email providers to block
const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
  'interia.pl', 'o2.pl', 'wp.pl', 'onet.pl', 'poczta.onet.pl',
  'buziaczek.pl', 'gazeta.pl', 'tlen.pl', 'aol.com', 'icloud.com',
  'protonmail.com', 'mail.com', 'gmx.com', 'yandex.com', 'zoho.com'
];
interface DownloadReportEmailFormProps {
  quizSubmissionId?: string;
  score?: number;
  band?: string;
}
export const DownloadReportEmailForm = ({
  quizSubmissionId,
  score,
  band
}: DownloadReportEmailFormProps) => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Proszę podać adres email");
      return;
    }

    if (!privacyConsent) {
      toast.error("Proszę zaznaczyć zgodę na przetwarzanie danych osobowych");
      return;
    }

    // Check if email is from a free provider
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (emailDomain && FREE_EMAIL_DOMAINS.includes(emailDomain)) {
      toast.error("Proszę użyć służbowego adresu email (nie dozwolone są darmowe skrzynki pocztowe)");
      return;
    }
    setIsSubmitting(true);
    logger.info(`Submitting email for report download: ${quizSubmissionId}, ${email}`);
    try {
      // Track FB pixel event
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'ReportDownloadRequested', {
          quiz_id: quizSubmissionId,
          score: score,
          band: band
        });
      }
      const {
        data,
        error
      } = await supabase.functions.invoke('download-report-email', {
        body: {
          quizId: quizSubmissionId,
          email: email
        }
      });
      if (error) {
        logger.error(`Error submitting email: ${error}`);
        throw error;
      }
      logger.info(`Email submitted successfully: ${JSON.stringify(data)}`);
      setIsSuccess(true);
      toast.success("Dziękujemy! Raport zostanie wysłany na podany adres email.");

      // Track FB pixel event
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'ReportDownloadCompleted', {
          quiz_id: quizSubmissionId,
          score: score,
          band: band
        });
      }
    } catch (error: any) {
      logger.error(`Error in download report email submission: ${error}`);
      toast.error("Wystąpił błąd. Spróbuj ponownie.");
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center">
        <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-400" />
        <h3 className="text-2xl font-bold mb-2">Gotowe!</h3>
        <p className="text-lg opacity-90">
          Raport zostanie wysłany na adres: <strong>{email}</strong>
        </p>
      </div>;
  }
  if (!showEmailForm) {
    return <Button size="lg" onClick={() => setShowEmailForm(true)} className="bg-white text-primary hover:bg-white/95 shadow-xl text-lg px-8 py-6 h-auto flex items-center gap-3 mx-auto">
        <Download className="h-6 w-6" />
        Pobierz PDF
      </Button>;
  }
  return <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary/60" />
            <Input type="email" placeholder="Twój adres email" value={email} onChange={e => setEmail(e.target.value)} className="bg-white text-gray-900 border-none pl-12 py-6 text-lg placeholder:text-gray-500" required disabled={isSubmitting} />
          </div>
        </div>

        <div className="flex items-start gap-3 text-left">
          <Checkbox id="privacy-consent" checked={privacyConsent} onCheckedChange={checked => setPrivacyConsent(checked === true)} className="mt-1 border-white data-[state=checked]:bg-white data-[state=checked]:text-primary" disabled={isSubmitting} />
          <label htmlFor="privacy-consent" className="text-sm leading-relaxed opacity-90 cursor-pointer">
            Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z{" "}
            <a href="/privacy-policy" className="underline hover:opacity-80" target="_blank" rel="noopener noreferrer">
              polityką prywatności
            </a>
            . *
          </label>
        </div>

        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-white text-primary hover:bg-white/95 shadow-xl text-lg px-8 py-6 h-auto flex items-center justify-center gap-3">
          {isSubmitting ? <>
              <Loader2 className="h-6 w-6 animate-spin" />
              Wysyłanie...
            </> : <>
              <Download className="h-6 w-6" />
              Pobierz raport
            </>}
        </Button>
      </form>
    </div>;
};